import { message } from "antd"
import { refreshPoints } from "~/common/billing/pointAction"
import { fetchTaskDraft } from "~/common/tasks/taskDraft"
import { getRecreateProductInfo } from "~/common/video/recreateProductInfo"
import {
  getSafeVideoOptions,
  isImageTaskProcessingStatus,
  ModelFigureType,
} from "~/common/constants"
import {
  DEFAULT_BACKGROUND_MUSIC_MODE,
  getRecreateArtlineSourceUrl,
  getRecreateModelArtline,
  getRecreateModelImageUrl,
  type RecreateAudio,
  type RecreateModel,
} from "~/common/video/recreate"
import { listModelFigures } from "~/api/model"

import type { HotVideoState, HotVideoSetState, HotVideoGetState } from "./types"
import {
  requestGuard,
  promptSaver,
  getPromptState,
  getInputFingerprint,
} from "./shared"

export const createTaskDraftActions = (
  set: HotVideoSetState,
  get: HotVideoGetState,
): Pick<HotVideoState, "restoreTaskDraft"> => ({
  restoreTaskDraft: async (taskId) => {
    get().resetDraftFields()
    get().setTaskId(taskId)
    const draftRevision = get().draftRevision
    const isStale = requestGuard.begin(
      () => get().draftRevision !== draftRevision,
    )
    let resumedPolling = false
    const models = new Map<string, API.Model.ModelFigure>()
    const loadModel = async (
      modelId: string,
      page = 0,
    ): Promise<API.Model.ModelFigure | undefined> => {
      if (isStale()) return undefined
      const cached = models.get(modelId)
      if (cached) return cached
      const response = await listModelFigures({ page, page_size: 50 })
      if (isStale()) return undefined
      const items = response.data?.items || []
      items.forEach((item) => models.set(item.model_id, item))
      const model = models.get(modelId)
      if (
        model ||
        !items.length ||
        (page + 1) * 50 >= (response.data?.total || 0)
      )
        return model
      return loadModel(modelId, page + 1)
    }
    const restoreModel = async (
      figure: API.FrameCopy.ModelFigureInfo | null,
    ): Promise<RecreateModel> => {
      if (!figure) return { mode: "none" }
      if (figure.type === ModelFigureType.manual)
        return { mode: "custom", url: figure.model_url }
      return {
        mode: "system",
        model: (await loadModel(figure.model_id)) || {
          model_id: figure.model_id,
        },
      }
    }
    set({ isRestoringTaskDraft: true })
    try {
      const result = await fetchTaskDraft(taskId, {
        isStale,
        onDraft: async (draft) => {
          if (isStale()) return
          let referenceModel = await restoreModel(draft.model_figure)
          if (!draft.model_figure) {
            const url = getRecreateArtlineSourceUrl(draft.model_figure_artline)
            if (url) referenceModel = { mode: "custom", url }
          }
          if (referenceModel.mode === "custom")
            referenceModel = {
              ...referenceModel,
              artline: draft.model_figure_artline,
            }
          if (isStale()) return
          const modelUrl = getRecreateModelImageUrl(referenceModel)
          const productImageUrls = draft.productImageUrls.filter(
            (url, index, urls) =>
              url !== modelUrl ||
              index !== urls.length - 1 ||
              draft.parsedImageUrls.includes(url),
          )
          const referenceAudio: RecreateAudio | null = draft.timbre_url
            ? {
                url: draft.timbre_url,
                name: "参考音频",
                type: "audio/wav",
                uploadedAt: 0,
                trimmed: false,
              }
            : null
          const state = get()
          const videoOptions = getSafeVideoOptions({
            duration: draft.duration || state.videoOptions.duration,
            model: draft.model || state.videoOptions.model,
            ratio: draft.ratio || state.videoOptions.ratio,
            resolution: draft.resolution || state.videoOptions.resolution,
          })
          const inputs = {
            productInfo: getRecreateProductInfo(draft.productInfo),
            videoUrl: draft.sourceVideoUrl,
            videoImageUrls: productImageUrls.join("\n"),
            backgroundMusicMode:
              draft.backgroundMusicMode ?? DEFAULT_BACKGROUND_MUSIC_MODE,
          } as const
          set({
            ...inputs,
            referenceModel,
            savedReferenceModel: referenceModel,
            referenceAudio,
            start_time: draft.start_time,
            end_time:
              draft.end_time ||
              (inputs.videoUrl === state.videoUrl ? state.end_time : 0),
            savedReferenceSettings: {
              video_url: draft.sourceVideoUrl,
              start_time: draft.start_time,
              end_time: draft.end_time,
              model_figure: draft.model_figure,
              timbre_url: draft.timbre_url,
            },
            ...getPromptState(draft.replicaPrompt),
            canSaveScript: draft.canSavePrompt,
            ...(inputs.videoUrl !== state.videoUrl
              ? { videoDuration: undefined }
              : {}),
            videoOptions,
            sourcePrompt: draft.sourcePrompt || "",
            taskErrorMessage: draft.errorMessage || "",
            replicaPromptInputFingerprint: getInputFingerprint(inputs),
            scriptRevision: state.scriptRevision + 1,
          })
          promptSaver.reset(
            draft.canSavePrompt ? taskId : "",
            get().replicaPrompt,
          )
        },
        onPollingStart: () => {
          if (isStale()) return
          resumedPolling = true
          set({ isRequesting: true, status: "AI拆解融合中..." })
        },
      })
      if (isStale() || !result) return
      if (resumedPolling) set({ status: "拆解融合完成" })
    } catch (error) {
      if (isStale()) return
      const errorMessage =
        error instanceof Error ? error.message : "获取任务草稿失败"
      set({
        taskErrorMessage: errorMessage,
        ...(resumedPolling ? { status: "拆解融合失败" } : {}),
      })
      void message.error(errorMessage)
    } finally {
      if (resumedPolling) void refreshPoints()
      if (!isStale()) set({ isRequesting: false, isRestoringTaskDraft: false })
    }
    if (!isStale()) {
      const artline = getRecreateModelArtline(get().referenceModel)
      if (artline && isImageTaskProcessingStatus(artline.status))
        void get().generateModelFigureArtline({ resume: true })
    }
  },
})
